/**
 * The menu atmosphere layer: motes drifting up behind the chrome.
 *
 * One component, used by both the main menu and the leaderboard — the design draws the
 * same field on both, and two copies of a scatter this fiddly would drift apart the
 * first time either was touched.
 *
 * The layout is computed ONCE per seed and cached at module scope. Nothing about it
 * depends on props or state, and recomputing it per render would restart every CSS
 * animation on any parent re-render — which is exactly what a leaderboard that refetches
 * every thirty seconds would do to it.
 */

/** Enough to read as a field, few enough to stay a compositor job. From the design. */
const PARTICLE_COUNT = 34;

/** The two shapes the design uses; the shard is the rarer one. */
const DOT = '/assets/p_dot2.png';
const SHARD = '/assets/p_shard4.png';

interface Mote {
  left: number;
  top: number;
  size: number;
  src: string;
  duration: number;
  delay: number;
}

const cache = new Map<number, readonly Mote[]>();

/**
 * A 32-bit mixer, returning [0, 1).
 *
 * The design places motes with `(i * 9301 + seed * 49297) % 233280`, which is not a hash
 * — for consecutive `i` it just adds 9301, so the x positions come out as an exact ramp
 * of 3.99% steps that wraps once across the field. In a still frame that reads as "some
 * dots"; in motion it is a marching diagonal comb. The seed only shifted its phase, so
 * both screens got the same comb in different places.
 *
 * This mixes properly, so the scatter looks scattered and the seed actually re-deals it.
 */
function hash01(n: number): number {
  let h = Math.imul(n ^ 0x9e37_79b9, 0x85eb_ca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2_ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4_294_967_296;
}

/**
 * A fixed scatter, not a random one.
 *
 * Deterministic on purpose: `Math.random()` here would re-deal the field on every mount,
 * so walking from the menu to the leaderboard and back would visibly reshuffle it.
 */
function motesFor(seed: number): readonly Mote[] {
  const cached = cache.get(seed);
  if (cached) return cached;

  const motes: Mote[] = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const k = i + seed * 1013;
    motes.push({
      left: hash01(k * 2) * 100,
      // Kept off the very top and bottom: a mote that starts its rise already at the
      // ceiling spends its whole life fading out behind the header.
      top: 15 + hash01(k * 2 + 1) * 80,
      size: (2 + ((i + seed) % 3) * 2) * 2,
      src: i % 5 === 0 ? SHARD : DOT,
      duration: 9 + (i % 7) * 1.6,
      delay: (i % 11) * 0.7,
    });
  }
  cache.set(seed, motes);
  return motes;
}

/** `seed` only picks WHICH scatter — pass a different one per screen so the two do not
 *  look like the same wallpaper. */
export function MenuParticles({ seed = 1 }: { seed?: number }) {
  return (
    <div className="fx-particles" aria-hidden="true">
      {motesFor(seed).map((m, i) => (
        <img
          key={i}
          src={m.src}
          alt=""
          style={{
            left: `${m.left}%`,
            top: `${m.top}%`,
            width: `${m.size}px`,
            height: `${m.size}px`,
            animationDuration: `${m.duration}s`,
            animationDelay: `${m.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
